// src/components/Sections/Admin/CodigosManuales/Create/index.jsx
import React, { useState, useEffect } from 'react';
import {
  Card, Form, Input, Select, InputNumber, Switch, Button,
  Divider, Alert, Radio, Typography, Spin, App
} from 'antd';
import {
  SaveOutlined, ArrowLeftOutlined, PercentageOutlined,
  DollarOutlined, TagOutlined, InfoCircleOutlined
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import CodigosManuales from '../../../../../service/CodigosManuales';
import Talleres from '../../../../../service/Talleres';
import { useNotify, useWithNotify } from '../../../../../providers/NotificationProvider';

const { Title, Text } = Typography;
const { TextArea } = Input;

const formatCLP = (value) => value ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.') : '';
const parseCLP  = (value) => value ? Number(value.replace(/\./g, '')) : undefined;

const CodigoManualForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const notify = useNotify();
  const withNotify = useWithNotify();
  const { modal } = App.useApp();

  const [form] = Form.useForm();
  const [loading, setLoading]           = useState(false);
  const [saving, setSaving]             = useState(false);
  const [talleres, setTalleres]         = useState([]);
  const [talleresLoading, setTalleresLoading] = useState(false);
  const [tipoDescuento, setTipoDescuento] = useState('porcentaje');

  const isEditMode = !!id;

  const loadTalleres = async () => {
    try {
      setTalleresLoading(true);
      const res = await Talleres.List({ allTalleres: true });
      setTalleres(res.talleres || []);
    } catch {
      notify.error('Error al cargar talleres');
    } finally {
      setTalleresLoading(false);
    }
  };

  const loadCodigo = async () => {
    try {
      setLoading(true);
      const res = await CodigosManuales.Get(id);
      form.setFieldsValue({
        codigo:               res.codigo,
        nombre:               res.nombre,
        descripcion:          res.descripcion,
        tipoDescuento:        res.tipoDescuento,
        valorDescuento:       res.valorDescuento,
        talleresAplicables:   res.talleresAplicables?.map(t => t._id || t) || [],
        usoMaximo:            res.usoMaximo || null,
        activo:               res.activo,
      });
      setTipoDescuento(res.tipoDescuento);
    } catch (error) {
      notify.error('Error al cargar código');
      navigate('/admin/codigos-manuales');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTalleres();
    if (isEditMode) loadCodigo();
  }, [id]);

  const handleSubmit = async (values) => {
    try {
      setSaving(true);
      const payload = {
        codigo:             values.codigo.trim().toUpperCase(),
        nombre:             values.nombre.trim(),
        descripcion:        values.descripcion?.trim() || '',
        tipoDescuento:      values.tipoDescuento,
        valorDescuento:     values.valorDescuento,
        talleresAplicables: values.talleresAplicables || [],
        usoMaximo:          values.usoMaximo || null,
        activo:             values.activo !== undefined ? values.activo : true,
      };

      if (isEditMode) {
        await withNotify(CodigosManuales.Update(id, payload), {
          loading: 'Actualizando...', success: 'Código actualizado', error: 'Error al actualizar'
        });
      } else {
        await withNotify(CodigosManuales.Create(payload), {
          loading: 'Creando...', success: 'Código creado', error: 'Error al crear'
        });
      }
      navigate('/admin/codigos-manuales');
    } catch (error) {
      notify.error(error.message || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    modal.confirm({
      title: '¿Descartar cambios?',
      okText: 'Descartar', okType: 'danger',
      cancelText: 'Continuar editando',
      onOk: () => navigate('/admin/codigos-manuales'),
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
      <Card className="shadow-lg rounded-lg max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/admin/codigos-manuales')} className="mb-4">
            Volver
          </Button>
          <Title level={3} className="!mb-1 flex items-center gap-2">
            <TagOutlined className="text-amber-500" />
            {isEditMode ? 'Editar Código Manual' : 'Crear Código Manual'}
          </Title>
          <Text type="secondary">
            Códigos que el vendedor ingresa manualmente para aplicar descuentos en ventas presenciales
          </Text>
        </div>

        <Alert
          message="Códigos manuales"
          description="Estos códigos se entregan a grupos especiales (bomberos, médicos, funcionarios municipales, etc.). El vendedor los ingresa en el paso de Plan de Pago durante una venta presencial."
          type="info"
          icon={<InfoCircleOutlined />}
          showIcon
          className="mb-6"
        />

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ activo: true, tipoDescuento: 'porcentaje', talleresAplicables: [] }}
        >
          {/* Código + Nombre */}
          <Card title="Identificación" size="small" className="mb-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Form.Item
                label="Código"
                name="codigo"
                rules={[
                  { required: true, message: 'Ingresa el código' },
                  { max: 30, message: 'Máximo 30 caracteres' },
                  { pattern: /^[A-Z0-9_-]+$/, message: 'Solo mayúsculas, números, guiones' },
                ]}
                tooltip="El texto exacto que el vendedor escribirá en caja. Ej: BOMBEROS2025"
              >
                <Input
                  placeholder="BOMBEROS2025"
                  maxLength={30}
                  className="font-mono uppercase"
                  onChange={e => form.setFieldValue('codigo', e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, ''))}
                />
              </Form.Item>

              <Form.Item
                label="Estado"
                name="activo"
                valuePropName="checked"
              >
                <Switch checkedChildren="Activo" unCheckedChildren="Inactivo" />
              </Form.Item>
            </div>

            <Form.Item
              label="Nombre"
              name="nombre"
              rules={[
                { required: true, message: 'Ingresa el nombre' },
                { min: 3, message: 'Mínimo 3 caracteres' },
                { max: 100, message: 'Máximo 100 caracteres' },
              ]}
            >
              <Input placeholder="Ej: Descuento Bomberos" maxLength={100} />
            </Form.Item>

            <Form.Item label="Descripción (opcional)" name="descripcion">
              <TextArea rows={2} placeholder="Ej: Descuento para personal de Bomberos de Ñuñoa" maxLength={250} showCount />
            </Form.Item>
          </Card>

          {/* Tipo y valor */}
          <Card title="Descuento" size="small" className="mb-4">
            <Form.Item label="Tipo" name="tipoDescuento" rules={[{ required: true }]}>
              <Radio.Group onChange={e => setTipoDescuento(e.target.value)} buttonStyle="solid">
                <Radio.Button value="porcentaje"><PercentageOutlined /> Porcentaje</Radio.Button>
                <Radio.Button value="monto_fijo"><DollarOutlined /> Monto Fijo</Radio.Button>
              </Radio.Group>
            </Form.Item>

            <Form.Item
              label={tipoDescuento === 'porcentaje' ? 'Porcentaje' : 'Monto a descontar'}
              name="valorDescuento"
              rules={[
                { required: true, message: 'Ingresa el valor' },
                { type: 'number', min: 1, message: 'Debe ser mayor a 0' },
                ...(tipoDescuento === 'porcentaje' ? [{ type: 'number', max: 100, message: 'Máximo 100%' }] : []),
              ]}
            >
              {tipoDescuento === 'porcentaje' ? (
                <InputNumber min={1} max={100} suffix="%" placeholder="20" className="w-full" />
              ) : (
                <InputNumber
                  min={1} prefix="$" placeholder="5.000"
                  formatter={formatCLP} parser={parseCLP}
                  className="w-full"
                />
              )}
            </Form.Item>

            <Form.Item
              label="Límite de usos (opcional)"
              name="usoMaximo"
              tooltip="Cuántas veces puede usarse este código en total. Vacío = sin límite."
            >
              <InputNumber min={1} placeholder="Sin límite" className="w-full" />
            </Form.Item>
          </Card>

          {/* Talleres */}
          <Card title="Talleres aplicables" size="small" className="mb-4">
            <Form.Item
              name="talleresAplicables"
              tooltip="Deja vacío para que aplique a todos los talleres"
            >
              <Select
                mode="multiple"
                placeholder={talleresLoading ? 'Cargando...' : 'Todos los talleres (vacío = todos)'}
                loading={talleresLoading}
                disabled={talleresLoading}
                options={talleres.map(t => ({
                  label: `${t.nombre} — ${t.recinto?.nombre || 'Sin recinto'}`,
                  value: t._id,
                }))}
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                showSearch
                maxTagCount="responsive"
                allowClear
              />
            </Form.Item>
            <Text type="secondary" className="text-xs">
              Si no seleccionas talleres, el código aplica a todos.
            </Text>
          </Card>

          <Divider />
          <div className="flex justify-end gap-3">
            <Button size="large" onClick={handleCancel}>Cancelar</Button>
            <Button
              type="primary" size="large" icon={<SaveOutlined />}
              htmlType="submit" loading={saving}
              style={{ background: '#feda2c', borderColor: '#feda2c', color: '#0d1117' }}
            >
              {isEditMode ? 'Guardar Cambios' : 'Crear Código'}
            </Button>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default CodigoManualForm;
